/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {DataExportApiService, DataExportModel} from './data-export-api.service';
import {DataExportCopyModalComponent} from './data-export-copy-modal.component';
import {DataExportEditPageComponent} from './data-export-edit-page.component';
import {DataExportListPageComponent} from './data-export-list-page.component';

export function dataExportStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(DataExportApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const localUserProfile = adnRouterHelper.services.localUserProfile();
  const readOnly = () => !permissions.hasNetworkPermission(PERMISSIONS.axAdvertiserAdmin.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.axPublisherAdmin.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisherTeamAdmin.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiserTeamAdmin.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.downloadLogs.apiKey);
  const listConfig = () => localUserProfile.getCurrentApp() === 'DATA'
    || (!permissions.hasNetworkPermission(PERMISSIONS.manageSystem.apiKey)
      && !permissions.hasNetworkPermission(PERMISSIONS.downloadLogs.apiKey))
    ? {where: 'source in STATS'}
    : {where: 'source in RAW_LOG,STATS'};
  const rh = adnRouterHelper.createRouterHelper('data-exports.data-export', DataExportApiService, 'app.adm', 'app/admin');
  const copyConfig = {
    modalComponent: DataExportCopyModalComponent,
    stateUrl: 'app.adm.data-exports.data-export',
    lcase: 'data export',
    ucase: 'Data Export'
  };

  states.add(rh.listPath(), rh.list({
    data: {app: ['DATA', 'ADVERTISING']},
    resolve: {listReadOnly: readOnly, dataExportListConfig: listConfig},
    views: {
      'content@app': {
        component: DataExportListPageComponent,
        bindings: {readOnly: 'listReadOnly', listConfig: 'dataExportListConfig'}
      },
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      dataExportPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<DataExportModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api, 'Data Export', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Data Export', 'name', copyConfig));
      },
      model: function(dataExportPage: SignalAdminResourcePageState<DataExportModel>) {
        return dataExportPage.model();
      }
    },
    views: {
      'content@app': {component: DataExportEditPageComponent, bindings: {state: 'dataExportPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'dataExportPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'dataExportPage'}}
    }
  }));
  return states.build();
}
